import { Lock } from '@phosphor-icons/react';
import { PetCareLockScreen } from './PetCareLockScreen';
import { FlipClock } from './widgets/ClockWidget';
import { LightsWidget } from './widgets/LightsWidget';

// Fullscreen lock screen overlaid on the kiosk dashboard when a household
// member has remotely locked this device. Variants are chosen per-device
// from Settings → Kiosks (see lockVariant on /api/kiosk/state):
//   'clock'    → the dashboard flip clock at a larger size (default)
//   'petcare'  → the pet-care feeding schedule (sitter mode)
//   'showings' → open-house greeter: clock + welcome + basement light switch.
//                Temporary; remove once the house sells.

export type LockVariant = 'clock' | 'petcare' | 'showings';

export function LockOverlay({ variant = 'clock' }: { variant?: LockVariant }) {
  if (variant === 'petcare') return <PetCareLockScreen />;
  if (variant === 'showings') return <ShowingsLockScreen />;
  return <ClockLockScreen />;
}

// Reuses the dashboard's flip clock at a larger size so a glance at the screen
// still tells time. Lock icon + label sit pinned to the bottom-center so the
// clock can stay optically centered in the viewport.
function ClockLockScreen() {
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-canvas text-fg"
      role="status"
      aria-live="polite"
    >
      <FlipClock size="large" />
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-fg-tertiary">
        <Lock size={48} weight="light" aria-hidden="true" />
        <span className="text-sm uppercase tracking-widest">Locked</span>
      </div>
    </div>
  );
}

// Open-house greeter shown during showings. Large flip clock up top so the
// room still reads as a clock at a glance, a short welcome, then the kiosk's
// real lights switch so visitors can flip the basement lights themselves.
// Reuses the dashboard LightsWidget verbatim — same entities, same toggle.
// Temporary surface; delete this screen (and its 'showings' variant) once the
// house sells.
function ShowingsLockScreen() {
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-canvas px-12 py-8 text-fg"
      role="status"
      aria-live="polite"
    >
      {/* Header: welcome text top-left, small clock top-right — the clock
          mirrors the pet-care lock screen. */}
      <header className="flex items-start justify-between gap-20 mt-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-6xl font-semibold tracking-tight mt-4 max-w-3xl">Welcome to our home.</h1>
          <p className="text-3xl text-fg-secondary">111 Longdon Ln</p>
        </div>
        <FlipClock size="default" className="flex-none" />
      </header>
      {/* Grey instructions sit just above the centered light switch. */}
      <div className="flex flex-1 flex-col items-center justify-center gap-12 text-center">
        <div className="flex flex-col gap-1">
          <p className="text-2xl text-fg-primary">
            We have smart lights in the basement.
          </p>
          <p className="text-2xl text-fg-secondary">
            Please use this switch to turn them on and off.
          </p>
        </div>
        {/* Sized box: LightsWidget fills its parent (w-full h-full pill), so the
            wrapper defines the vertical toggle's footprint. Match the dashboard
            card exactly — there it renders col-span-2 (~93px) in the 256px
            row-2 track of the 1080px-wide kiosk grid, so the switch keeps the
            same tall-and-narrow proportions here instead of looking chunky. */}
        <div className="h-[256px] w-[93px]">
          <LightsWidget className="h-full w-full" />
        </div>
      </div>
    </div>
  );
}
